// How a running count gets from one true figure to the next.
//
// The ledger's totals move in steps: nothing for four seconds, then a whole
// slice — a few million bytes, a few hundred million pixels — all at once.
// That is what happens, and it reads as a counter that is mostly asleep. The
// work is not asleep: bytes arrive and are hashed the whole time. So the
// figure on screen climbs steadily from the total it showed to the total the
// ledger now holds, and is timed to arrive about when the next slice will.
//
// THE ONE RULE: the figure shown never passes the figure checked. A ramp runs
// from what was on screen up to the ledger's total and stops there; it never
// extrapolates, so a stalled network or a paused video leaves the counter
// standing on a true number rather than running on without anything to count.
//
// Pure and tested for the usual reason (see panelModel.ts): vitest here is
// node-only, so the component that draws this can decide nothing.

export interface Ramp {
  from: number
  to: number
  startMs: number
  durationMs: number
}

/** A slice every four seconds is the steady state; these bound the odd ones.
 * The floor keeps the burst of slices at the start of playback from being
 * over in a blink, the ceiling keeps a long stall from setting a crawl. */
export const MIN_RAMP_MS = 1200
export const MAX_RAMP_MS = 6000
/** Before two totals have been seen there is no gap to measure. */
export const FIRST_RAMP_MS = 2500

/** A ramp that is already where it is going. */
export function restingRamp(value: number, nowMs: number): Ramp {
  return { from: value, to: value, startMs: nowMs, durationMs: 0 }
}

/** The figure to show at `nowMs`. Linear, and clamped at both ends. */
export function rampValue(ramp: Ramp, nowMs: number): number {
  if (ramp.durationMs <= 0) return ramp.to
  const progress = (nowMs - ramp.startMs) / ramp.durationMs
  if (progress <= 0) return ramp.from
  if (progress >= 1) return ramp.to
  return ramp.from + (ramp.to - ramp.from) * progress
}

export function rampDone(ramp: Ramp, nowMs: number): boolean {
  return nowMs >= ramp.startMs + ramp.durationMs
}

/** How long the climb to a new total should take, given how long ago the
 * last total arrived: about that long again, within the bounds above. */
export function rampDurationFor(gapMs: number | null): number {
  if (gapMs === null || !Number.isFinite(gapMs)) return FIRST_RAMP_MS
  return Math.min(MAX_RAMP_MS, Math.max(MIN_RAMP_MS, gapMs))
}

/**
 * Points a ramp at a new total, carrying on from wherever it had got to.
 *
 * A total that went DOWN is a different video (the ledger rebuilt), not a
 * correction, so the counter is put there at once rather than counted
 * backwards.
 */
export function retarget(ramp: Ramp, target: number, nowMs: number, durationMs: number): Ramp {
  const shown = rampValue(ramp, nowMs)
  if (target <= shown) return restingRamp(target, nowMs)
  return { from: shown, to: target, startMs: nowMs, durationMs }
}
